import { isBackendEnabled as backendConfigured } from '../config/env';
import { tapDamage as combatTapDamage } from '../game/deterministic-combat';
import {
  clientItemToItemId,
  equippedIdToClientWeapon,
  inferCategory,
  isFrontendWeapon,
  parseEquippedWeaponId,
  resolveServerItem,
  resolveServerWeapon,
  WOOD_CLUB_ID,
} from '../game/catalog';
import { defaultLevel } from '../config/env';
import {
  parsePendingDrops,
  parseSessionDrops,
  normalizeGeodesList,
  resolveBreakDustPieces,
} from '../game/loot';
import { clearGeodeEntry, gameSyncState, getGeodeSyncEntry, snapshotBreakLoot, type GeodeSyncEntry } from '../game/server-state';
import {
  applyEquippedFromServer,
  applyInventoryFromServer,
  applyProfileFromServer,
  playerState,
  refreshHud,
} from '../game/player-state';
import { emitGame, GameEvents } from '../game/events';
import { coalesceHudRefresh, scheduleIdle } from './schedule';
import { getBaseUrl } from './api-base';
import { workerFetch } from './network-client';
import { clearSession, getSessionToken } from './session';

export { getBaseUrl } from './api-base';

const WOOD_CLUB = 'WoodClub';
const BATCH_DEBOUNCE_MS = 500;
const FULL_DEBOUNCE_MS = 5000;
const PROFILE_POLL_MS = 10_000;
const MAX_GEODE_HIT_BATCH = 20;

let fullTimer: ReturnType<typeof setTimeout> | null = null;
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let batchFlushPromise: Promise<unknown> | null = null;
let pendingClientBatchId: string | null = null;
let profilePollTimer: ReturnType<typeof setInterval> | null = null;
let profilePollInFlight = false;
let profileFetchGeneration = 0;
let energyRegenTimer: ReturnType<typeof setInterval> | null = null;
let refreshInFlight = false;

export class BackendError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, { status, body }: { status: number; body?: unknown }) {
    super(message);
    this.name = 'BackendError';
    this.status = status;
    this.body = body;
  }
}

export function isBackendEnabled() {
  return backendConfigured() && Boolean(getSessionToken());
}

export function invalidateInFlightProfilePolls() {
  profileFetchGeneration += 1;
}

function getAuthHeaders(): Record<string, string> {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path: string, options: RequestInit = {}) {
  const url = `${getBaseUrl()}${path}`;
  const res = await workerFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(options.headers ?? {}),
    },
  });

  const body = res.status === 204 ? null : await res.json().catch(() => null);

  if (res.status === 401) {
    clearSession();
    window.dispatchEvent(new CustomEvent('booga:session-expired'));
  }

  if (!res.ok) {
    const errBody = body as { message?: string } | null;
    throw new BackendError(errBody?.message || res.statusText, { status: res.status, body });
  }
  return body;
}

function parseProfileTimestamp(raw: unknown) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  return Number.isFinite(Date.parse(s)) ? s : null;
}

export function normalizeProfile(raw: Record<string, unknown> = {}) {
  return {
    energy: Number(raw.Energy ?? raw.energy ?? 0),
    maxEnergy: Number(raw.MaxEnergy ?? raw.maxEnergy ?? 100),
    dust: Number(raw.Dust ?? raw.dust ?? 0),
    dustCollected: Number(raw.dustCollected ?? raw.TotalDust ?? 0),
    nextEnergyAt: parseProfileTimestamp(raw.nextEnergyAt ?? raw.NextEnergyAt),
    energyUpdatedAt: parseProfileTimestamp(raw.energyUpdatedAt ?? raw.EnergyUpdatedAt),
  };
}

export function normalizeInventoryItem(raw: Record<string, unknown> = {}) {
  const category = String(raw.Category ?? raw.category ?? 'misc');
  const weaponId = (raw.WeaponId ?? raw.weaponId) as number | null;
  const itemId = (raw.ItemId ?? raw.itemId) as number | null;

  let name = String(raw.Name ?? raw.name ?? '');
  if (category === 'weapon' || weaponId != null) {
    const resolved = resolveServerWeapon({ WeaponId: weaponId, Name: name });
    if (resolved?.clientName) name = resolved.clientName;
  } else if (itemId != null) {
    const resolvedItem = resolveServerItem({ ItemId: itemId, Name: name });
    if (resolvedItem) name = resolvedItem;
  }

  const item: {
    name: string;
    image: string;
    quantity: number;
    category: string;
    weaponId?: number;
    itemId?: number;
    hp?: number;
  } = {
    name,
    image: String(raw.Image ?? raw.image ?? ''),
    quantity: Number(raw.Quantity ?? raw.quantity ?? 0),
    category,
  };
  if (weaponId != null) item.weaponId = Number(weaponId);
  if (itemId != null) item.itemId = Number(itemId);
  if (raw.Hp != null || raw.hp != null) item.hp = Number(raw.Hp ?? raw.hp);
  return item;
}

function normalizePendingDrops(raw: Record<string, unknown> = {}) {
  const parsed = parsePendingDrops(raw);
  const itemRaw = (raw.PendingDrops ?? raw.pendingDrops) as Record<string, unknown> | null;
  const nestedItem = itemRaw?.item ?? itemRaw?.Item;
  return {
    dust: parsed.dust,
    item: nestedItem ? normalizeInventoryItem(nestedItem as Record<string, unknown>) : null,
  };
}

export function normalizeGeode(raw: Record<string, unknown> = {}) {
  return {
    location: String(raw.Location ?? raw.location ?? ''),
    geodeType: String(raw.GeodeType ?? raw.geodeType ?? 'Dust Geode'),
    hp: Number(raw.Hp ?? raw.hp ?? 0),
    geodeId: (raw.GeodeId ?? raw.geodeId) as string | number | null,
    combatSeed:
      raw.CombatSeed != null || raw.combatSeed != null
        ? Number(raw.CombatSeed ?? raw.combatSeed)
        : null,
    tapsApplied: Number(raw.TapsApplied ?? raw.tapsApplied ?? 0),
    pendingDrops: normalizePendingDrops(raw),
  };
}

function reapplyOptimisticGeodeHp(
  unackedTaps: number,
  tapsApplied: number,
  serverHp: number,
  geodeId: string | number | null,
  combatSeed: number | null,
  weaponName = playerState.equipped.weapon ?? WOOD_CLUB,
) {
  let hp = Number(serverHp ?? 0);
  const pending = Math.max(0, Number(unackedTaps ?? 0));
  if (pending <= 0 || combatSeed == null || !geodeId) return hp;
  for (let k = 0; k < pending; k++) {
    hp = Math.max(0, hp - combatTapDamage(combatSeed, geodeId, (tapsApplied ?? 0) + k, weaponName));
  }
  return hp;
}

function mergeGeodeHitAck(g: ReturnType<typeof normalizeGeode>, { sentCount = 0 } = {}) {
  if (!g.location || g.location !== gameSyncState.levelName) return null;
  const prev = getGeodeSyncEntry();
  const geodeIdChanged = Boolean(prev?.geodeId && g.geodeId && prev.geodeId !== g.geodeId);

  if (geodeIdChanged) {
    return applyGeodeRow(g, { resetUnacked: true });
  }

  const prevCommitted = prev?.tapsApplied ?? 0;
  const serverCommitted = g.tapsApplied ?? 0;
  if (prev?.geodeId && g.geodeId && prev.geodeId === g.geodeId && prevCommitted > serverCommitted) {
    if (g.pendingDrops && (g.pendingDrops.dust > 0 || g.pendingDrops.item)) {
      prev.pendingDrops = g.pendingDrops;
      gameSyncState.geodeEntry = prev;
    }
    return prev;
  }

  const unackedTaps = Math.max(0, (prev?.unackedTaps ?? 0) - Math.max(0, sentCount));
  const geodeId = g.geodeId ?? prev?.geodeId ?? null;
  const combatSeed = g.combatSeed ?? prev?.combatSeed ?? null;
  const entry: GeodeSyncEntry = {
    geodeType: g.geodeType,
    geodeId,
    combatSeed,
    tapsApplied: serverCommitted,
    pendingDrops: g.pendingDrops ?? prev?.pendingDrops ?? { dust: 0, item: null },
    unackedTaps,
    hp: reapplyOptimisticGeodeHp(unackedTaps, serverCommitted, g.hp, geodeId, combatSeed),
    awaitingBreakAck: g.hp > 0 ? false : (prev?.awaitingBreakAck ?? false),
  };
  gameSyncState.geodeEntry = entry;
  emitGame(GameEvents.GEODE_SYNC, { hp: entry.hp, broke: entry.hp <= 0 });
  return entry;
}

function applyGeodeRow(g: ReturnType<typeof normalizeGeode>, { resetUnacked = false } = {}) {
  if (g.location !== gameSyncState.levelName) return null;
  const prev = getGeodeSyncEntry();
  const geodeIdChanged = Boolean(prev?.geodeId && g.geodeId && prev.geodeId !== g.geodeId);
  const unackedTaps = resetUnacked || geodeIdChanged ? 0 : (prev?.unackedTaps ?? 0);
  const tapsApplied = g.tapsApplied ?? prev?.tapsApplied ?? 0;
  const geodeId = g.geodeId ?? prev?.geodeId ?? null;
  const combatSeed = g.combatSeed ?? prev?.combatSeed ?? null;
  const entry: GeodeSyncEntry = {
    geodeType: g.geodeType,
    hp: reapplyOptimisticGeodeHp(unackedTaps, tapsApplied, g.hp, geodeId, combatSeed),
    geodeId,
    combatSeed,
    tapsApplied,
    pendingDrops: g.pendingDrops ?? prev?.pendingDrops ?? { dust: 0, item: null },
    unackedTaps,
    awaitingBreakAck: geodeIdChanged ? false : (prev?.awaitingBreakAck ?? false),
  };
  gameSyncState.geodeEntry = entry;
  emitGame(GameEvents.GEODE_SYNC, { hp: entry.hp, broke: entry.hp <= 0 });
  return entry;
}

function pickServerGeode(geodes: ReturnType<typeof normalizeGeode>[]) {
  if (!geodes.length) return null;

  const candidates = [
    gameSyncState.levelName,
    defaultLevel,
    'DEMO',
    'Booga Town',
    ...geodes.map((g) => g.location),
  ];

  for (const loc of candidates) {
    const match = geodes.find((g) => g.location === loc);
    if (match) return match;
  }

  return geodes[0];
}

function applyGeodeMap(geodes: ReturnType<typeof normalizeGeode>[]) {
  const picked = pickServerGeode(geodes);
  if (!picked) {
    clearGeodeEntry();
    return;
  }

  gameSyncState.levelName = picked.location;
  applyGeodeRow(picked, { resetUnacked: true });
}

function normalizeDrops(raw: Record<string, unknown> = {}) {
  const drops = parseSessionDrops(raw);
  if (!drops) return null;
  const out: { dust?: number; item?: Record<string, unknown> } = {};
  if (drops.dust > 0) out.dust = drops.dust;
  if (drops.item) out.item = drops.item;
  if (drops.dust === 0 && !drops.item && (raw.drops != null || raw.Drops != null)) {
    out.dust = 0;
  }
  return Object.keys(out).length ? out : null;
}

function parseEquippedFromResponse(data: Record<string, unknown> | null | undefined) {
  const id = data?.equippedWeaponId;
  if (id !== undefined && id !== null) return id as number;
  const nameField = (data?.equippedWeaponName ?? data?.equippedWeapon) as string | null;
  if (nameField != null && nameField !== '') return parseEquippedWeaponId(nameField);
  if (id === null) return null;
  return undefined;
}

function equippedIdToWeaponName(equippedWeaponId: number | null | undefined) {
  if (equippedWeaponId == null || equippedWeaponId === WOOD_CLUB_ID) return WOOD_CLUB;
  for (const [name, id] of Object.entries(gameSyncState.weaponIdByName)) {
    if (id === equippedWeaponId && isFrontendWeapon(name)) return name;
  }
  return equippedIdToClientWeapon(equippedWeaponId) ?? WOOD_CLUB;
}

function clientWeaponToWeaponId(weaponName: string | null) {
  if (!weaponName || weaponName === WOOD_CLUB) return null;
  const id = gameSyncState.weaponIdByName[weaponName];
  return id === WOOD_CLUB_ID ? null : id ?? null;
}

function inventoryToServer() {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];

  for (const slot of playerState.inventorySlots) {
    if (!slot?.name || slot.count <= 0 || seen.has(slot.name)) continue;
    if (slot.name === WOOD_CLUB) continue;
    seen.add(slot.name);
    const row: Record<string, unknown> = {
      Name: slot.name,
      Image: '',
      Quantity: slot.count,
      Category: inferCategory(slot.name),
    };
    const wid = clientWeaponToWeaponId(slot.name);
    if (wid != null) row.WeaponId = wid;
    const iid = clientItemToItemId(slot.name);
    if (iid != null) row.ItemId = iid;
    out.push(row);
  }
  return out;
}

function persistenceSnapshotFromState() {
  return {
    profile: {
      Dust: playerState.dust,
      dustCollected: gameSyncState.dustCollected,
    },
    inventory: inventoryToServer(),
  };
}

function applySessionResponse(body: Record<string, unknown> | null, { geodeHitSentCount = null as number | null } = {}) {
  if (!body) return null;

  const prevEntry = getGeodeSyncEntry();
  const pendingDust = Math.max(
    prevEntry?.pendingDrops?.dust ?? 0,
    gameSyncState.breakLootSnapshot,
  );
  const sessionDrops = normalizeDrops(body);
  const hasDropsField = body.drops != null || body.Drops != null;

  const bodyGeode = body.geode as Record<string, unknown> | undefined;
  const nextGeode = bodyGeode ? normalizeGeode(bodyGeode) : null;
  const nextGeodeId = nextGeode?.geodeId ?? null;
  const geodeIdChanged = Boolean(
    prevEntry?.geodeId && nextGeodeId && prevEntry.geodeId !== nextGeodeId,
  );
  const tapsReset = Boolean(
    prevEntry && prevEntry.tapsApplied > 0 && (nextGeode?.tapsApplied ?? -1) === 0,
  );
  const profileDustGrant = body.profile
    ? Math.max(
        0,
        normalizeProfile(body.profile as Record<string, unknown>).dust -
          gameSyncState.lastServerProfileDust,
      )
    : 0;
  const dustPieces = resolveBreakDustPieces({
    sessionDust: sessionDrops?.dust ?? 0,
    pendingDust,
    geodeIdChanged,
    tapsReset,
    profileDustGrant,
    hasDropsField,
  });

  if (body.profile) {
    const profile = normalizeProfile(body.profile as Record<string, unknown>);
    applyProfileFromServer(profile, { deferDustGrant: dustPieces });
  }

  gameSyncState.breakLootSnapshot = 0;

  let geodeResult: GeodeSyncEntry | null = null;
  if (body.geode) {
    const g = normalizeGeode(body.geode as Record<string, unknown>);
    if (g.location) {
      if (geodeHitSentCount != null) {
        geodeResult = mergeGeodeHitAck(g, { sentCount: geodeHitSentCount });
      } else {
        geodeResult = applyGeodeRow(g, { resetUnacked: true });
      }
    }
  }

  if (body.weapon) {
    applyInventoryFromServer([normalizeInventoryItem(body.weapon as Record<string, unknown>)], { merge: true });
  }

  const sessionItem = sessionDrops?.item as Record<string, unknown> | undefined;
  if (sessionDrops?.item) {
    applyInventoryFromServer([normalizeInventoryItem(sessionDrops.item)], { merge: true });
  }

  const equippedId = parseEquippedFromResponse(body);
  const equippedName = (body.equippedWeaponName ?? body.equippedWeapon) as string | null;
  if (equippedId !== undefined) {
    gameSyncState.equippedWeaponId = equippedId as number | null;
    let weaponName: string | null = null;
    if (equippedName) {
      const resolved = resolveServerWeapon({ Name: equippedName });
      weaponName = resolved?.clientName ?? equippedName;
    }
    if (!weaponName) weaponName = equippedIdToWeaponName(equippedId as number);
    applyEquippedFromServer(weaponName);
  }

  if (dustPieces > 0 || sessionItem) {
    const itemName = sessionItem ? String(sessionItem.Name ?? sessionItem.name ?? '') : '';
    emitGame(GameEvents.SERVER_DROPS, {
      dust: dustPieces,
      items: itemName ? [itemName] : [],
    });
  }

  scheduleIdle(() => coalesceHudRefresh(refreshHud));
  return geodeResult;
}

export async function applyServerSnapshot(snapshot: {
  profile?: ReturnType<typeof normalizeProfile>;
  inventory?: ReturnType<typeof normalizeInventoryItem>[];
  geodes?: ReturnType<typeof normalizeGeode>[];
  equippedWeaponId?: number | null;
  equippedWeaponName?: string | null;
}) {
  if (snapshot.profile) applyProfileFromServer(snapshot.profile);
  const inv = (snapshot.inventory ?? []).map((i) => normalizeInventoryItem(i as unknown as Record<string, unknown>));
  applyInventoryFromServer(inv);

  if (snapshot.geodes) applyGeodeMap(snapshot.geodes);

  const equippedId = snapshot.equippedWeaponId;
  if (equippedId !== undefined) {
    let weaponName = snapshot.equippedWeaponName
      ? resolveServerWeapon({ Name: snapshot.equippedWeaponName })?.clientName ?? snapshot.equippedWeaponName
      : null;
    if (!weaponName) weaponName = equippedIdToWeaponName(equippedId);
    applyEquippedFromServer(weaponName);
    gameSyncState.equippedWeaponId = equippedId;
  }

  gameSyncState.backendSynced = true;
  coalesceHudRefresh(refreshHud);
}

export async function fetchPlayerSnapshot() {
  const data = (await request('/player')) as Record<string, unknown>;
  const equippedId = parseEquippedFromResponse(data);
  return {
    profile: normalizeProfile((data.profile ?? data) as Record<string, unknown>),
    equippedWeaponId: equippedId as number | null | undefined,
    equippedWeaponName: (data.equippedWeaponName as string) ?? null,
    inventory: ((data.inventory as unknown[]) ?? []).map((row) =>
      normalizeInventoryItem(row as Record<string, unknown>),
    ),
    geodes: normalizeGeodesList(data).map((row) => normalizeGeode(row)),
    displayName: (data.displayName ?? data.DisplayName) as string | null,
    email: (data.email ?? data.Email) as string | null,
    playerId: (data.playerId ?? data.PlayerId) as number | null,
  };
}

export async function fetchPlayerIdentity() {
  const data = (await request('/player')) as Record<string, unknown>;
  return {
    displayName: (data.displayName ?? data.DisplayName) as string | null,
    email: (data.email ?? data.Email) as string | null,
    playerId: (data.playerId ?? data.PlayerId) as number | null,
  };
}

export async function fetchSmashMeta() {
  try {
    const data = (await request('/meta')) as Record<string, unknown>;
    return { energyRegenSeconds: Number(data?.energyRegenSeconds) || 300 };
  } catch {
    return { energyRegenSeconds: 300 };
  }
}

async function patchGeodeHitRaw(payload: Record<string, unknown>, { keepalive = false } = {}) {
  const url = `${getBaseUrl()}/player/session`;
  const res = await workerFetch(url, {
    method: 'PATCH',
    keepalive,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  const body = res.status === 204 ? null : await res.json().catch(() => null);
  if (res.status === 401) {
    clearSession();
    window.dispatchEvent(new CustomEvent('booga:session-expired'));
  }
  return { status: res.status, body: body as Record<string, unknown> | null };
}

function newClientBatchId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `batch-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function flushGeodeHitBatch({ keepalive = false } = {}) {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }

  if (batchFlushPromise) await batchFlushPromise;

  const entry = getGeodeSyncEntry();
  if (!entry) return null;
  const hitCount = entry.unackedTaps ?? 0;
  if (hitCount <= 0) return null;
  if (!entry.geodeId) {
    void refreshProfileFromServer();
    return null;
  }

  const sentCount = Math.min(hitCount, MAX_GEODE_HIT_BATCH);
  const batchId = pendingClientBatchId ?? newClientBatchId();
  if (!pendingClientBatchId) pendingClientBatchId = batchId;

  const payload: Record<string, unknown> = {
    action: 'geode_hit',
    location: gameSyncState.levelName,
    hitCount: sentCount,
    clientBatchId: batchId,
    geodeId: entry.geodeId,
  };

  batchFlushPromise = (async () => {
    try {
      const { status, body } = await patchGeodeHitRaw(payload, { keepalive });
      if (body) {
        pendingClientBatchId = null;
        applySessionResponse(body, { geodeHitSentCount: sentCount });
      }
      if (status === 409 || (status === 400 && String(body?.message ?? '').includes('Geode'))) {
        void reconcileSessionFromServer();
      }
      return body?.geode ? normalizeGeode(body.geode as Record<string, unknown>) : null;
    } catch (err) {
      console.warn('[backend] PATCH geode_hit failed:', err);
      return null;
    } finally {
      batchFlushPromise = null;
      const pending = getGeodeSyncEntry()?.unackedTaps ?? 0;
      if (pending > 0) scheduleBatchFlush(sentCount >= MAX_GEODE_HIT_BATCH);
    }
  })();

  return batchFlushPromise;
}

function scheduleBatchFlush(immediate = false) {
  if (!isBackendEnabled()) return;
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  if (immediate) {
    void flushGeodeHitBatch();
    return;
  }
  batchTimer = setTimeout(() => {
    batchTimer = null;
    void flushGeodeHitBatch();
  }, BATCH_DEBOUNCE_MS);
}

export function enqueueGeodeTapSync(immediate = false) {
  if (!isBackendEnabled()) return;
  const entry = getGeodeSyncEntry();
  if (!entry?.geodeId) {
    void refreshProfileFromServer();
    return;
  }
  invalidateInFlightProfilePolls();
  entry.unackedTaps = (entry.unackedTaps ?? 0) + 1;
  if (!pendingClientBatchId) pendingClientBatchId = newClientBatchId();
  scheduleBatchFlush(immediate);
}

async function sendFullSync() {
  if (!isBackendEnabled()) return;
  try {
    const res = (await request('/player', {
      method: 'PUT',
      body: JSON.stringify(persistenceSnapshotFromState()),
    })) as Record<string, unknown>;
    await applyServerSnapshot({
      profile: normalizeProfile((res.profile as Record<string, unknown>) ?? {}),
      equippedWeaponId: parseEquippedFromResponse(res) as number | null,
      equippedWeaponName: (res.equippedWeaponName as string) ?? null,
      inventory: ((res.inventory as unknown[]) ?? []).map((row) =>
        normalizeInventoryItem(row as Record<string, unknown>),
      ),
    });
  } catch (err) {
    console.warn('[backend] PUT /player failed:', err);
  }
}

function scheduleFull(immediate = false) {
  if (!isBackendEnabled()) return;
  if (fullTimer) clearTimeout(fullTimer);
  if (immediate) {
    fullTimer = null;
    return sendFullSync();
  }
  fullTimer = setTimeout(() => {
    fullTimer = null;
    void sendFullSync();
  }, FULL_DEBOUNCE_MS);
}

export function queueSync(tier: 'light' | 'light-immediate' | 'full' | 'full-immediate' = 'light') {
  if (!isBackendEnabled()) return;
  switch (tier) {
    case 'light-immediate':
      enqueueGeodeTapSync(true);
      break;
    case 'full-immediate':
      scheduleFull(true);
      break;
    case 'full':
      scheduleFull(false);
      break;
    default:
      enqueueGeodeTapSync(false);
      break;
  }
}

export async function flushSync() {
  if (!isBackendEnabled()) return null;
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  if (batchFlushPromise) {
    try {
      return await batchFlushPromise;
    } catch {
      return null;
    }
  }
  if ((getGeodeSyncEntry()?.unackedTaps ?? 0) > 0) {
    return flushGeodeHitBatch({ keepalive: true });
  }
  return null;
}

export async function flushFullSync() {
  if (!isBackendEnabled()) return;
  if (fullTimer) {
    clearTimeout(fullTimer);
    fullTimer = null;
  }
  await flushSync();
  await sendFullSync();
}

export function syncEquipWeapon(weaponName: string) {
  if (!isBackendEnabled()) return;
  void (async () => {
    try {
      await flushSync();
      const payload: Record<string, unknown> = {
        action: 'equip_weapon',
        weapon: clientWeaponToWeaponId(weaponName),
        location: gameSyncState.levelName,
      };
      const res = (await request('/player/session', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })) as Record<string, unknown>;
      applySessionResponse(res);
    } catch (err) {
      console.warn('[backend] equip_weapon failed:', err);
    }
  })();
}

async function reconcileSessionFromServer() {
  try {
    const snapshot = await fetchPlayerSnapshot();
    await applyServerSnapshot(snapshot);
  } catch (err) {
    console.warn('[backend] reconcile failed:', err);
  }
}

function hasPendingCombatSync() {
  const unacked = getGeodeSyncEntry()?.unackedTaps ?? 0;
  return unacked > 0 || Boolean(batchTimer) || Boolean(batchFlushPromise);
}

function mergeProfileFromPoll(profile: ReturnType<typeof normalizeProfile>, fetchGeneration: number) {
  if (fetchGeneration !== profileFetchGeneration) return false;

  playerState.maxEnergy = profile.maxEnergy;
  const pendingCombat = hasPendingCombatSync();
  if (!pendingCombat) {
    if (gameSyncState.pendingBreakDust > 0) {
      playerState.energy = profile.energy;
      playerState.dust = profile.dust - gameSyncState.pendingBreakDust;
      playerState.dustCollected = profile.dustCollected - gameSyncState.pendingBreakDust;
      gameSyncState.dustCollected = playerState.dustCollected;
      playerState.nextEnergyAt = profile.nextEnergyAt;
      playerState.energyUpdatedAt = profile.energyUpdatedAt ?? null;
      if (profile.energy >= profile.maxEnergy) {
        playerState.nextEnergyAt = null;
        playerState.energyUpdatedAt = null;
      }
    } else {
      applyProfileFromServer(profile);
    }
  } else {
    playerState.nextEnergyAt = profile.nextEnergyAt;
    playerState.energyUpdatedAt = profile.energyUpdatedAt;
  }
  coalesceHudRefresh(refreshHud);
  return true;
}

export async function refreshProfileFromServer() {
  if (!isBackendEnabled() || profilePollInFlight) return false;
  profilePollInFlight = true;
  const gen = profileFetchGeneration;
  try {
    const snapshot = await fetchPlayerSnapshot();
    if (!getGeodeSyncEntry()?.geodeId && snapshot.geodes.length) {
      applyGeodeMap(snapshot.geodes);
    }
    mergeProfileFromPoll(snapshot.profile, gen);
    return true;
  } catch (err) {
    console.warn('[backend] profile refresh failed:', err);
    return false;
  } finally {
    profilePollInFlight = false;
  }
}

export function startProfilePoll() {
  stopProfilePoll();
  if (!isBackendEnabled()) return;
  profilePollTimer = setInterval(() => {
    if (!gameSyncState.appVisible) return;
    void refreshProfileFromServer();
  }, PROFILE_POLL_MS);
}

export function stopProfilePoll() {
  if (profilePollTimer) {
    clearInterval(profilePollTimer);
    profilePollTimer = null;
  }
}

export function startEnergyRegenTicker() {
  stopEnergyRegenTicker();
  if (!isBackendEnabled()) return;
  energyRegenTimer = setInterval(() => {
    if (!gameSyncState.appVisible) return;
    const { energy, maxEnergy, nextEnergyAt } = playerState;
    if (energy >= maxEnergy || !nextEnergyAt) return;
    if (Date.now() < new Date(nextEnergyAt).getTime()) return;
    if (!refreshInFlight) {
      refreshInFlight = true;
      void refreshProfileFromServer().finally(() => {
        refreshInFlight = false;
      });
    }
  }, 250);
}

export function stopEnergyRegenTicker() {
  if (energyRegenTimer) {
    clearInterval(energyRegenTimer);
    energyRegenTimer = null;
  }
}

export function onAppBackground() {
  gameSyncState.appVisible = false;
  void flushFullSync();
}

export function onAppForeground() {
  gameSyncState.appVisible = true;
  void refreshProfileFromServer();
}

export async function syncFromServer() {
  if (!isBackendEnabled()) return false;
  try {
    const [snapshot, meta] = await Promise.all([fetchPlayerSnapshot(), fetchSmashMeta()]);
    gameSyncState.energyRegenSeconds = meta.energyRegenSeconds;
    gameSyncState.levelName = defaultLevel;
    await applyServerSnapshot(snapshot);
    startProfilePoll();
    startEnergyRegenTicker();
    return true;
  } catch (err) {
    if (err instanceof BackendError && err.status === 401) {
      stopProfilePoll();
      stopEnergyRegenTicker();
    } else {
      console.warn('[backend] syncFromServer failed:', err);
    }
    return false;
  }
}

export function getOptimisticTapDamage() {
  const entry = getGeodeSyncEntry();
  if (!entry || entry.combatSeed == null || !entry.geodeId) return 1;
  const tapIndex = (entry.tapsApplied ?? 0) + (entry.unackedTaps ?? 0);
  return combatTapDamage(
    entry.combatSeed,
    entry.geodeId,
    tapIndex,
    playerState.equipped.weapon ?? WOOD_CLUB,
  );
}

export function applyOptimisticGeodeTap() {
  const entry = getGeodeSyncEntry();
  if (!entry?.geodeId) {
    void refreshProfileFromServer();
    return entry?.hp ?? 0;
  }
  const dmg = getOptimisticTapDamage();
  entry.hp = Math.max(0, entry.hp - dmg);
  if (entry.hp <= 0) snapshotBreakLoot();
  enqueueGeodeTapSync(false);
  return entry.hp;
}

export function hasServerGeode() {
  return Boolean(getGeodeSyncEntry()?.geodeId);
}
